import { BLAST_TTL_TICKS, WEAPONS, vec, type PlayerId, type State, type Vec2, type WeaponType } from '@chessshooter/shared';
import { ARENA_AUDIO } from './uiConstants';

export type ArenaEvent =
  | { kind: 'shot'; weapon: WeaponType; pos: Vec2; dir: Vec2; ownerId: PlayerId }
  | { kind: 'explosion' | 'pickup' | 'hit'; pos: Vec2; ownerId: PlayerId };

/** Lifetimes identify volleys without changing the wire format; consume confirmed states only. */
export class ArenaEvents {
  private previous: State | null = null;

  reset(state?: State): void { this.previous = state ?? null; }

  update(state: State): ArenaEvent[] {
    const previous = this.previous;
    if (previous && state.tick <= previous.tick) return [];
    this.previous = state;
    if (!previous || state.winner !== null || state.phase !== 'shooter' || previous.phase !== 'shooter' ||
      state.transitionTicks > 0 || previous.transitionTicks > 0 || state.tick - previous.tick > ARENA_AUDIO.maxGapTicks) return [];
    const events: ArenaEvent[] = [];
    const volleys = new Set<string>();
    for (const p of [...state.projectiles.filter(p => !p.fromNpc), ...state.beams]) {
      const born = state.tick - (WEAPONS[p.weapon].ttlTicks - p.ttlTicks);
      if (born < previous.tick) continue;
      const key = `${p.ownerId}:${p.weapon}:${born}`;
      if (volleys.has(key)) continue;
      volleys.add(key);
      const dir = 'dir' in p ? p.dir : vec.normalize(p.vel);
      const player = state.players[p.ownerId];
      events.push({ kind: 'shot', weapon: p.weapon, ownerId: p.ownerId,
        pos: 'from' in p ? p.from : player.pos, dir });
    }
    for (const player of state.players) {
      const old = previous.players[player.id];
      // An immediately absorbed shot may leave no projectile in the next snapshot.
      if (old.weapon && !player.weapon && !events.some(e => e.kind === 'shot' && e.ownerId === player.id && e.weapon === old.weapon)) {
        events.push({ kind: 'shot', weapon: old.weapon, ownerId: player.id, pos: old.pos, dir: { x: 0, y: 0 } });
      }
      if (player.weapon && player.weapon !== old.weapon) events.push({ kind: 'pickup', pos: player.pos, ownerId: player.id });
      if (player.hitTimeLostMs > old.hitTimeLostMs) events.push({ kind: 'hit', pos: player.pos, ownerId: player.id });
    }
    for (const blast of state.blasts) {
      if (state.tick - (BLAST_TTL_TICKS - blast.ttlTicks) >= previous.tick) {
        events.push({ kind: 'explosion', pos: blast.pos, ownerId: blast.ownerId });
      }
    }
    return events;
  }
}
